/**
 * Main App Component - Commerce and Marketplace Application
 */

import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Store
import { store } from './store';

// Services
import { initializeAuth } from './store/slices/authSlice';
import { useAppDispatch } from './store';

// Pages
import { Landing, Solutions, Pricing, Resources, Studio, VSCodeExtension } from './pages';
import Marketplace from './pages/Marketplace';
import './styles/global.css';

// Lazy load components for code splitting
const Dashboard = React.lazy(() => import('./components/dashboard/DeveloperDashboard'));

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// App initialization component
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Initialize authentication on app load
    dispatch(initializeAuth());
  }, [dispatch]);

  return <>{children}</>;
};

// Loading fallback component
const LoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// SEO Meta component
const SEOHead: React.FC = () => {
  React.useEffect(() => {
    // Set page title and meta tags
    document.title = 'Relay — The serverless control plane for teams that ship with confidence';

    // Create or update meta tags
    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Basic meta tags
    setMeta('description', 'Relay unifies deployment, governance, observability, and monetization for serverless functions across every cloud. Ship fast, stay compliant, and grow revenue.');
    setMeta('keywords', 'serverless platform, function control plane, multi-cloud functions, serverless observability, usage based billing, function marketplace, governance');
    setMeta('author', 'Relay');
    setMeta('robots', 'index, follow');

    // Open Graph tags
    setProperty('og:title', 'Relay — Operate every function with confidence');
    setProperty('og:description', 'Relay unifies deployment, policy, observability, and revenue for serverless teams across clouds.');
    setProperty('og:type', 'website');
    setProperty('og:url', window.location.href);
    setProperty('og:site_name', 'Relay');

    // Twitter Card tags
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Relay — The serverless control plane');
    setMeta('twitter:description', 'Run every function with insight and control. Deployment, policy, observability, and monetization in one platform.');

    // Theme color
    setMeta('theme-color', '#5B5BD6');

    // Structured data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Relay Protocol",
      "description": "Serverless control plane for deploying, governing, and monetizing reusable functions",
      "url": window.location.origin,
      "logo": `${window.location.origin}/assets/relay-protocol-logo.svg`,
      "sameAs": [
        "https://github.com/relay-protocol"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "technical support",
        "url": "https://github.com/relay-protocol"
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData, null, 2);

  }, []);

  return null;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <AppInitializer>
          <Router>
            <>
              <SEOHead />
              <div className="App app-shell">
                <Routes>
                  {/* Landing Page */}
                  <Route path="/" element={<Landing />} />

                  {/* Marketplace Routes */}
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/studio" element={<Studio />} />

                  {/* VS Code Extension Page */}
                  <Route path="/vscode" element={<VSCodeExtension />} />

                  {/* Protected Routes */}
                  <Route
                    path="/dashboard/*"
                    element={
                      <Suspense fallback={<LoadingFallback />}>
                        <Dashboard />
                      </Suspense>
                    }
                  />

                  {/* Fallback Route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>

                {/* Global Toast Notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 5000,
                    style: {
                      background: 'var(--surface-overlay)',
                      color: 'var(--color-text)',
                      border: '1px solid var(--surface-border)',
                      boxShadow: '0 16px 40px rgba(15, 23, 42, 0.35)'
                    },
                    success: {
                      style: {
                        background: 'var(--color-success-bg)',
                        color: 'var(--color-success)',
                      },
                    },
                    error: {
                      style: {
                        background: 'var(--color-danger-bg)',
                        color: 'var(--color-danger)',
                      },
                    },
                  }}
                />
              </div>
            </>
          </Router>
        </AppInitializer>
      </QueryClientProvider>
    </Provider>
  );
};

export default App;
